import { Component, inject, OnInit } from '@angular/core';
import {
  ButtonClickEvent,
  CacBaseDialogComponent,
  CacButtonComponent,
  CacDialogLayoutComponent,
  CacFieldComponent,
  CacTabComponent,
  CacTableComponent,
  CacTabsComponent,
  table,
} from '@controladad/ng-base';
import { UserVendorDetail, VendorItem } from '@models';
import { UserApiService, OrderApiService } from '../../../services';
import { StatusValues } from '../../../data';
import { formControl, formGroup, Validators } from '@al00x/forms';
import { cacTableIdCol } from '../../table';
import { NgIf } from '@angular/common';
import { of } from 'rxjs';
import { AuthStore } from '../../../states';

export interface VendorUserDialogData {
  vendor?: VendorItem;
}

export type VendorUserDialogResult = boolean;

@Component({
  selector: 'lib-vendor-dialog',
  standalone: true,
  imports: [
    CacDialogLayoutComponent,
    CacTableComponent,
    CacFieldComponent,
    CacButtonComponent,
    CacTabsComponent,
    CacTabComponent,
    NgIf,
  ],
  templateUrl: './vendor-user-dialog.component.html',
  styleUrl: './vendor-user-dialog.component.scss',
})
export class VendorUserDialogComponent
  extends CacBaseDialogComponent<VendorUserDialogData, VendorUserDialogResult>
  implements OnInit {
  private userApi = inject(UserApiService);
  private orderApi = inject(OrderApiService);
  private vendorDetail: UserVendorDetail | undefined = undefined;
  readonly isRestrictedAdmin = inject(AuthStore).isRestrictedAdmin();

  form = formGroup({
    vendorName: formControl<string>(undefined, Validators.required),
    status: formControl<number>(1), //Enabled
    email: formControl<string>(undefined, [Validators.required, Validators.email]),
    password: formControl<string>(undefined),
    passwordConfirm: formControl<string>(undefined),
  });

  StatusValues = StatusValues;

  ngOnInit() {
    const form = this.form.controls;
    const vendor = this.data.vendor;
    if (vendor) {
      this.userApi.getVendorDetails(vendor?.id).subscribe((r) => {
        this.form.patchValue(r.vendorDetailsDTO);
        this.vendorDetail = r.vendorDetailsDTO;
      });
      form.password.setValidators([Validators.password()]);
      form.passwordConfirm.setValidators([Validators.match(form.password)]);
    } else {
      form.password.setValidators([Validators.required, Validators.password()]);
      form.passwordConfirm.setValidators([Validators.required, Validators.match(form.password)]);
    }
    form.passwordConfirm.updateValueAndValidity();
    form.password.updateValueAndValidity();
  }

  ordersTable = table({
    itemsFn: this.data?.vendor?.userId ? this.orderApi.getAdapter({
      default: {
        filters: [
          { key: 'VendorUserId', value: this.data.vendor.userId }
        ]
      }
    }) : () => of([]),
    view: {
      title: 'Orders',
    },
    columns: {
      id: cacTableIdCol(),
      purchaseDate: {
        label: 'Order Date',
        sortable: true,
        filterable: [
          { type: 'greater', label: 'From', controlType: 'date', key: 'PurchaseDateFrom' },
          { type: 'lower', label: 'To', controlType: 'date', key: 'PurchaseDateTo' },
        ],
        type: 'datetime',
      },
    },
  })

  productsTable = table({
    itemsFn: this.userApi.getVendorProducts(this.data.vendor?.id),
    view: {
      title: 'Products',
    },
    columns: {
      id: { label: 'Product ID', sortable: true, filterable: true },
      'name.english': {
        label: 'English Name',
        sortable: true,
        filterable: 'EnglishName',
      },
      'name.arabic': {
        label: 'Arabic Name',
        sortable: true,
        filterable: 'ArabicName',
      },
      price: {
        label: 'Price',
        sortable: true,
        filterable: [
          { type: 'greater', label: 'Min Price', inputType: 'number-nobtn', key: 'PriceFrom' },
          { type: 'lower', label: 'Max Price', inputType: 'number-nobtn', key: 'PriceTo' },
        ],
      },
    },
  });

  onSubmit(e: ButtonClickEvent) {
    const values = this.form.getRawValue();

    if (this.data.vendor) {
      // Only include password if it has a value
      const updateValues: any = { ...values, id: this.vendorDetail?.userId };
      if (!values.password) {
        updateValues.password = undefined;
      }
      if (!values.passwordConfirm) {
        updateValues.passwordConfirm = undefined;
      }

      this.userApi.updateVendor(updateValues)
        .pipe(e.pipe())
        .subscribe(() => {
          this.submit(true);
        });
    } else {
      this.userApi.createVendor(values)
        .pipe(e.pipe())
        .subscribe(() => {
          this.submit(true);
        });
    }
  }

  onDelete(e: ButtonClickEvent) {
    const vendor = this.data.vendor;
    if (!vendor) return;

    dialog$
      .deletePrompt(false, { itemName: `Vendor ${vendor.vendorName ?? vendor.id}` })
      .afterSubmit()
      .subscribe(() => {
        this.userApi
          .deleteVendor(vendor.id)
          .pipe(e.pipe())
          .subscribe(() => {
            this.submit(true);
            snackbar$.show('Delete Successful');
          });
      });
  }

  onForceLogout(e: ButtonClickEvent) {
    // TODO: UPDATE, API MISSING
    // this.data.user &&
    //   this.userApi
    //     .forceLogout(this.data.user.id)
    //     .pipe(e.pipe())
    //     .subscribe(() => {
    //       this.dialog.close();
    //       snackbar$.show('Force Login Successful');
    //     });
  }
}
